import { useState, useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { CampaignContext } from '~/context/campaign';
import { setCampaignContribution } from '~/services/registration';
import { identityOptions } from '~/shared/config';
import {
    TextInput,
    SelectInput,
    CheckboxInput,
    currencyConfig,
} from '~/shared/components/form/Inputs';
import IntlCurrencyInput from 'react-intl-currency-input';
import { Button, Col, Row, Form, FormGroup, Input, Label } from 'reactstrap';
import { Loading } from '~/shared/components/Loading';
import { fetchMyCampaign } from '../../services/campaign';
import moment from 'moment';
import { AdminContext } from '~/context/admin';
import toast from 'react-hot-toast';

const KNOWN_FIELDS = new Set([
    'fin_num_in_household',
    'fin_is_single_parent_household',
    'fin_is_irs_dependent',
    'fin_monthly_wages',
    'fin_soc_sec_income',
    'fin_disability_income',
    'fin_other_income',
    'fin_tot_monthly_income',
    'fin_anl_household_income',
    'fin_other_comments',
    'fin_certify_identity',
    'fin_signature',
    'fin_signature_date',
    'fin_has_hospital_costs',
    'fin_has_medication_costs',
    'fin_has_travel_costs',
    'fin_has_phys_therapy_costs',
    'fin_has_copay_costs',
    'fin_has_home_healthcare_costs',
    'fin_has_post_care_costs',
    'fin_has_living_donor_costs',
    'fin_has_donor_search',
    'fin_has_med_equip_costs',
    'fin_has_home_modification_costs',
    'fin_has_transportation_costs',
    'fin_has_emergency_living_expenses',
]);

const booleanOptions = [
    {
        label: 'Yes',
        value: true,
    },
    {
        label: 'No',
        value: false,
    },
];

const expensesOptions = [
    {
        label: 'Hospital Costs',
        value: 'fin_has_hospital_costs',
    },
    {
        label: 'Medications',
        value: 'fin_has_medication_costs',
    },
    {
        label: 'Travel/Temporary Relocation',
        value: 'fin_has_travel_costs',
    },
    {
        label: 'Physical Therapy/Rehabilitation',
        value: 'fin_has_phys_therapy_costs',
    },
    {
        label: 'Co-Pay/Deductibles',
        value: 'fin_has_copay_costs',
    },
    {
        label: 'Home Health Care',
        value: 'fin_has_home_healthcare_costs',
    },
    {
        label: 'Post Care',
        value: 'fin_has_post_care_costs',
    },
    {
        label: 'Living Donor Costs',
        value: 'fin_has_living_donor_costs',
    },
    {
        label: 'Donor Search',
        value: 'fin_has_donor_search',
    },
    {
        label: 'Medical Equipment/Supplies',
        value: 'fin_has_med_equip_costs',
    },
    {
        label: 'Home Modifications',
        value: 'fin_has_home_modification_costs',
    },
    {
        label: 'Accessible Transportation',
        value: 'fin_has_transportation_costs',
    },
    {
        label: 'Emergency Living Expenses',
        value: 'fin_has_emergency_living_expenses',
    },
];

export const FinancialInformation = ({ }: any) => {
    const [readOnly, setReadOnly] = useState(false);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ campaignId }] = useContext(AdminContext);
    const today = moment().format('Y-MM-DD');
    const {
        handleSubmit,
        watch,
        control,
        reset,
        setValue,
        setError,
        formState: { errors, isSubmitting },
    } = useForm({
        mode: 'onBlur',
        defaultValues: {
            fin_num_in_household: '',
            // null until the user picks a Yes/No so RHF "required" can detect unselected
            fin_is_single_parent_household: null as boolean | null,
            fin_is_irs_dependent: null as boolean | null,
            fin_monthly_wages: 0,
            fin_soc_sec_income: 0,
            fin_disability_income: 0,
            fin_other_income: 0,
            fin_tot_monthly_income: '',
            fin_anl_household_income: '',
            fin_other_comments: '',
            fin_certify_identity: '',
            fin_signature: '',
            fin_signature_date: '',
            fin_has_hospital_costs: false,
            fin_has_medication_costs: false,
            fin_has_travel_costs: false,
            fin_has_phys_therapy_costs: false,
            fin_has_copay_costs: false,
            fin_has_home_healthcare_costs: false,
            fin_has_post_care_costs: false,
            fin_has_living_donor_costs: false,
            fin_has_donor_search: false,
            fin_has_med_equip_costs: false,
            fin_has_home_modification_costs: false,
            fin_has_transportation_costs: false,
            fin_has_emergency_living_expenses: false,
        },
    });

    const wageIncome = watch('fin_monthly_wages') || 0;
    const socIncome = watch('fin_soc_sec_income') || 0;
    const disabilityIncome = watch('fin_disability_income') || 0;
    const otherIncome = watch('fin_other_income') || 0;
    // Round to 2 decimal places to avoid floating-point precision errors
    const monthlyIncome = Math.round((wageIncome + socIncome + disabilityIncome + otherIncome) * 100) / 100;
    const annualIncome =
        monthlyIncome > 0 ? Math.round(monthlyIncome * 12 * 100) / 100 : 0;

    const history = useHistory();
    const location = useLocation<{ read_only?: boolean }>();

    const getCampaign = async () => {
        const resp = await fetchMyCampaign();
        if (resp.detail) {
            if (resp.detail === 'Invalid token.') {
                localStorage.removeItem('token');
                history.push('/login');
            }
        } else {
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
        }
    };

    useEffect(() => {
        if (location.state) {
            const { read_only } = location.state;
            if (read_only) {
                setReadOnly(read_only);
            }
        }
    }, [location]);

    useEffect(() => {
        if (!campaign) {
            getCampaign();
        } else {
            let app = campaign.campaignApplication || {};
            app['fin_monthly_wages'] = app['fin_monthly_wages']
                ? parseFloat(app['fin_monthly_wages'])
                : 0;
            app['fin_soc_sec_income'] = app['fin_soc_sec_income']
                ? parseFloat(app['fin_soc_sec_income'])
                : 0;
            app['fin_disability_income'] = app['fin_disability_income']
                ? parseFloat(app['fin_disability_income'])
                : 0;
            app['fin_other_income'] = app['fin_other_income']
                ? parseFloat(app['fin_other_income'])
                : 0;

            const {
                fin_num_in_household,
                fin_is_single_parent_household,
                fin_is_irs_dependent,
                fin_monthly_wages,
                fin_soc_sec_income,
                fin_disability_income,
                fin_other_income,
                fin_tot_monthly_income,
                fin_anl_household_income,
                fin_other_comments,
                fin_certify_identity,
                fin_signature,
                fin_signature_date,
                fin_has_hospital_costs,
                fin_has_medication_costs,
                fin_has_travel_costs,
                fin_has_phys_therapy_costs,
                fin_has_copay_costs,
                fin_has_home_healthcare_costs,
                fin_has_post_care_costs,
                fin_has_living_donor_costs,
                fin_has_donor_search,
                fin_has_med_equip_costs,
                fin_has_home_modification_costs,
                fin_has_transportation_costs,
                fin_has_emergency_living_expenses,
            } = app;

            reset({
                fin_num_in_household,
                fin_is_single_parent_household,
                fin_is_irs_dependent,
                fin_monthly_wages,
                fin_soc_sec_income,
                fin_disability_income,
                fin_other_income,
                fin_tot_monthly_income,
                fin_anl_household_income,
                fin_other_comments,
                fin_certify_identity,
                fin_signature,
                fin_signature_date,
                fin_has_hospital_costs,
                fin_has_medication_costs,
                fin_has_travel_costs,
                fin_has_phys_therapy_costs,
                fin_has_copay_costs,
                fin_has_home_healthcare_costs,
                fin_has_post_care_costs,
                fin_has_living_donor_costs,
                fin_has_donor_search,
                fin_has_med_equip_costs,
                fin_has_home_modification_costs,
                fin_has_transportation_costs,
                fin_has_emergency_living_expenses,
            });

            // Reset signature date
            setValue('fin_signature_date', today);
        }
    }, [campaign]);

    useEffect(() => {
        setValue('fin_tot_monthly_income', monthlyIncome);
    }, [monthlyIncome]);

    useEffect(() => {
        setValue('fin_anl_household_income', annualIncome);
    }, [annualIncome]);

    const onInvalid = () => {
        toast.error('Please fix the highlighted fields before saving');
    };

    const onSubmit = async (data) => {
        if (monthlyIncome < 1) {
            setError('fin_monthly_wages', {
                type: 'manual',
                message: 'Please provide at least one source of income',
            });
            toast.error('You must provide at least one source of income');
            return;
        }

        const loadingId = toast.loading('Saving financial information...');
        try {
            const resp = await setCampaignContribution(data, campaignId);
            toast.dismiss(loadingId);

            if (resp.status === 401) {
                localStorage.removeItem('token');
                window.dispatchEvent(new Event('session-expired'));
                history.push('/login');
                return;
            }

            if (resp.status === 403) {
                toast.error(
                    'You do not have permission to update this campaign'
                );
                return;
            }

            if (resp.status === 400 && resp.data && typeof resp.data === 'object') {
                let mappedAny = false;
                Object.entries(resp.data).forEach(([field, messages]) => {
                    if (KNOWN_FIELDS.has(field)) {
                        const message = Array.isArray(messages)
                            ? messages.join(' ')
                            : String(messages);
                        setError(field as any, {
                            type: 'server',
                            message,
                        });
                        mappedAny = true;
                    }
                });
                toast.error(
                    mappedAny
                        ? 'Please fix the highlighted fields'
                        : (resp.data?.detail as string) ||
                              'There was an error saving your financial information'
                );
                return;
            }

            if (!resp.ok) {
                const detail =
                    resp.data && typeof resp.data === 'object'
                        ? (resp.data as any).detail
                        : null;
                toast.error(
                    detail || 'There was an error saving your financial information'
                );
                return;
            }

            campaignDispatch({
                type: 'set_campaign',
                payload: resp.data,
            });
            toast.success('Financial information saved');
            history.goBack();
        } catch (e) {
            console.error('Error saving financial information:', e);
            toast.dismiss(loadingId);
            toast.error('There was an error saving your financial information');
        }
    };

    if (!campaign) {
        return <Loading />;
    }

    const requiredBoolean = (value: boolean | null) =>
        value === true || value === false || 'Please select an option';

    return (
        <div className='container__inner'>
            <Form onSubmit={handleSubmit(onSubmit, onInvalid)}>
                <Row className='mb-5'>
                    <Col lg='6'>
                        <h3>Financial Information</h3>
                        <p>
                            By collecting some of the tax information for your
                            household, we are able to better gauge what your
                            need is. A household usually includes the tax
                            filer, their spouse if they have one, and their
                            tax dependents.
                        </p>
                        <TextInput
                            name='fin_num_in_household'
                            label='Number in household?'
                            type='number'
                            control={control}
                            required={true}
                            ariaRequired={true}
                            readOnly={readOnly}
                        />
                        <Label>Is this a single parent household? *</Label>
                        <Controller
                            name='fin_is_single_parent_household'
                            control={control}
                            rules={{ validate: requiredBoolean }}
                            render={({ field, fieldState: { error } }) => (
                                <FormGroup className='mb-3'>
                                    <div className='d-flex' style={{ gap: '1rem' }}>
                                        {booleanOptions.map((opt, i) => (
                                            <Label check key={i}>
                                                <Input
                                                    type='radio'
                                                    value={opt.value.toString()}
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            e.currentTarget
                                                                .value === 'true'
                                                        )
                                                    }
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    checked={field.value === opt.value}
                                                    disabled={readOnly}
                                                />{' '}
                                                {opt.label}
                                            </Label>
                                        ))}
                                    </div>
                                    {error && (
                                        <span className='text-danger d-block mt-1'>
                                            {error.message}
                                        </span>
                                    )}
                                </FormGroup>
                            )}
                        />
                        <Label>
                            Does a parent, spouse or other relative claim you
                            as a dependent on their tax return? *
                        </Label>
                        <Controller
                            name='fin_is_irs_dependent'
                            control={control}
                            rules={{ validate: requiredBoolean }}
                            render={({ field, fieldState: { error } }) => (
                                <FormGroup className='mb-3'>
                                    <div className='d-flex' style={{ gap: '1rem' }}>
                                        {booleanOptions.map((opt, i) => (
                                            <Label check key={i}>
                                                <Input
                                                    type='radio'
                                                    value={opt.value.toString()}
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            e.currentTarget
                                                                .value === 'true'
                                                        )
                                                    }
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    checked={field.value === opt.value}
                                                    disabled={readOnly}
                                                />{' '}
                                                {opt.label}
                                            </Label>
                                        ))}
                                    </div>
                                    {error && (
                                        <span className='text-danger d-block mt-1'>
                                            {error.message}
                                        </span>
                                    )}
                                </FormGroup>
                            )}
                        />
                        <p>
                            Use the following to estimate your household total
                            monthly income. Household income is the combined
                            gross income of all members of a household who are
                            15 years or older, either related or non-related.
                        </p>

                        <FormGroup>
                            <Label>Monthly Salary/Wages/Tips</Label>
                            <Controller
                                name='fin_monthly_wages'
                                control={control}
                                render={({ field }) => (
                                    <IntlCurrencyInput
                                        value={field.value || 0}
                                        className='form-control'
                                        config={currencyConfig}
                                        onChange={(e, val) => {
                                            if (readOnly) return;
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                            {errors.fin_monthly_wages && (
                                <span className='text-danger d-block mt-1'>
                                    {errors.fin_monthly_wages.message}
                                </span>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label>Social Security</Label>
                            <Controller
                                name='fin_soc_sec_income'
                                control={control}
                                render={({ field }) => (
                                    <IntlCurrencyInput
                                        value={field.value || 0}
                                        className='form-control'
                                        config={currencyConfig}
                                        onChange={(e, val) => {
                                            if (readOnly) return;
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Social Security Disability</Label>
                            <Controller
                                name='fin_disability_income'
                                control={control}
                                render={({ field }) => (
                                    <IntlCurrencyInput
                                        value={field.value || 0}
                                        className='form-control'
                                        config={currencyConfig}
                                        onChange={(e, val) => {
                                            if (readOnly) return;
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                Other Income (such as capital gains, dividends,
                                interest, rents, pensions, alimony)
                            </Label>
                            <Controller
                                name='fin_other_income'
                                control={control}
                                render={({ field }) => (
                                    <IntlCurrencyInput
                                        value={field.value || 0}
                                        className='form-control'
                                        config={currencyConfig}
                                        onChange={(e, val) => {
                                            if (readOnly) return;
                                            field.onChange(val);
                                        }}
                                    />
                                )}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                Total monthly income (sum of the above)
                            </Label>
                            <IntlCurrencyInput
                                value={monthlyIncome}
                                className='form-control'
                                config={currencyConfig}
                                readOnly={true}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>
                                Annual household income (monthly income x 12)
                            </Label>
                            <IntlCurrencyInput
                                value={annualIncome}
                                className='form-control'
                                config={currencyConfig}
                                readOnly={true}
                            />
                        </FormGroup>
                        <Label className='mt-3'>
                            Please check all your anticipated out-of-pocket
                            medical and related expenses
                        </Label>
                        <FormGroup check className='mb-3'>
                            <Row>
                                {expensesOptions.map((opt, i) => {
                                    return (
                                        <Col md={6} key={i}>
                                            <CheckboxInput
                                                control={control}
                                                name={opt.value}
                                                label={opt.label}
                                            />
                                        </Col>
                                    );
                                })}
                            </Row>
                        </FormGroup>
                        <TextInput
                            name='fin_other_comments'
                            label='Other/additional comments'
                            type='textarea'
                            control={control}
                            readOnly={readOnly}
                        />

                        <hr />

                        <p>
                            I understand that information in this application
                            will be used by Help Hope Live to verify financial
                            need, as required by the IRS. I guarantee that the
                            above information is true to the best of my
                            knowledge.
                        </p>

                        <SelectInput
                            control={control}
                            required={true}
                            ariaRequired={true}
                            name='fin_certify_identity'
                            label='I certify that I am the:'
                            defaultLabel='Select'
                            options={identityOptions}
                            errors={errors?.fin_certify_identity?.message}
                            readOnly={readOnly}
                        />

                        <Row>
                            <Col>
                                <TextInput
                                    name='fin_signature'
                                    label='Name'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    readOnly={readOnly}
                                />
                            </Col>
                            <Col>
                                <TextInput
                                    name='fin_signature_date'
                                    label='Date'
                                    control={control}
                                    type='date'
                                    required={true}
                                    ariaRequired={true}
                                    readOnly={readOnly}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <div
                    className='sticky-bottom p-4 bg-white'
                    style={{
                        borderTop: '1px solid #0D0106',
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                    }}
                >
                    <Button
                        color='outline-dark'
                        type='button'
                        disabled={isSubmitting}
                        onClick={() => history.push('/patient/edit')}
                    >
                        Cancel
                    </Button>
                    {!readOnly && (
                        <Button
                            color='primary'
                            type='submit'
                            disabled={isSubmitting || readOnly}
                        >
                            {isSubmitting ? `Saving...` : `Save`}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    );
};
